import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import { PlannedIncomeDomainService } from '@app/domain/services';
import {
  GetPlannedIncomesUseCase,
  GetDuePlannedIncomesUseCase,
  GetPlannedIncomeByIdUseCase,
  CountPlannedIncomesUseCase,
  CreatePlannedIncomeUseCase,
  UpdatePlannedIncomeUseCase,
  DeletePlannedIncomeUseCase,
  DeactivatePlannedIncomeUseCase,
  ActivatePlannedIncomeUseCase,
  ActivateAllPlannedIncomesUseCase,
  GetCurrencyRateUseCase,
} from '@app/domain/use-cases';
import type { IPlannedIncomeRepository, IIncomeRepository, ITransactionManager } from '@app/domain/ports';
import {
  PLANNED_INCOME_REPOSITORY,
  INCOME_REPOSITORY,
  TRANSACTION_MANAGER,
} from '../../tokens/infrastructure.tokens';

/**
 * Provides PlannedIncome domain services and use cases.
 */
export function providePlannedIncomeDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: PlannedIncomeDomainService,
      useFactory: () => new PlannedIncomeDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetPlannedIncomesUseCase,
      useFactory: (repository: IPlannedIncomeRepository) => new GetPlannedIncomesUseCase(repository),
      deps: [PLANNED_INCOME_REPOSITORY],
    },
    {
      provide: GetDuePlannedIncomesUseCase,
      useFactory: (repository: IPlannedIncomeRepository) => new GetDuePlannedIncomesUseCase(repository),
      deps: [PLANNED_INCOME_REPOSITORY],
    },
    {
      provide: GetPlannedIncomeByIdUseCase,
      useFactory: (repository: IPlannedIncomeRepository) => new GetPlannedIncomeByIdUseCase(repository),
      deps: [PLANNED_INCOME_REPOSITORY],
    },
    {
      provide: CountPlannedIncomesUseCase,
      useFactory: (repository: IPlannedIncomeRepository) => new CountPlannedIncomesUseCase(repository),
      deps: [PLANNED_INCOME_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreatePlannedIncomeUseCase,
      useFactory: (repository: IPlannedIncomeRepository, domainService: PlannedIncomeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreatePlannedIncomeUseCase(repository, domainService, dispatch);
      },
      deps: [PLANNED_INCOME_REPOSITORY, PlannedIncomeDomainService],
    },
    {
      provide: UpdatePlannedIncomeUseCase,
      useFactory: (repository: IPlannedIncomeRepository, domainService: PlannedIncomeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdatePlannedIncomeUseCase(repository, domainService, dispatch);
      },
      deps: [PLANNED_INCOME_REPOSITORY, PlannedIncomeDomainService],
    },
    {
      provide: DeletePlannedIncomeUseCase,
      useFactory: (repository: IPlannedIncomeRepository, domainService: PlannedIncomeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeletePlannedIncomeUseCase(repository, domainService, dispatch);
      },
      deps: [PLANNED_INCOME_REPOSITORY, PlannedIncomeDomainService],
    },
    {
      provide: DeactivatePlannedIncomeUseCase,
      useFactory: (repository: IPlannedIncomeRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeactivatePlannedIncomeUseCase(repository, dispatch);
      },
      deps: [PLANNED_INCOME_REPOSITORY],
    },
    {
      provide: ActivatePlannedIncomeUseCase,
      useFactory: (
        plannedIncomeRepository: IPlannedIncomeRepository,
        incomeRepository: IIncomeRepository,
        getCurrencyRateUseCase: GetCurrencyRateUseCase,
        transactionManager: ITransactionManager,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new ActivatePlannedIncomeUseCase(
          plannedIncomeRepository,
          incomeRepository,
          getCurrencyRateUseCase,
          transactionManager,
          dispatch,
        );
      },
      deps: [PLANNED_INCOME_REPOSITORY, INCOME_REPOSITORY, GetCurrencyRateUseCase, TRANSACTION_MANAGER],
    },
    {
      provide: ActivateAllPlannedIncomesUseCase,
      useFactory: (
        plannedIncomeRepository: IPlannedIncomeRepository,
        incomeRepository: IIncomeRepository,
        getCurrencyRateUseCase: GetCurrencyRateUseCase,
        transactionManager: ITransactionManager,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new ActivateAllPlannedIncomesUseCase(
          plannedIncomeRepository,
          incomeRepository,
          getCurrencyRateUseCase,
          transactionManager,
          dispatch,
        );
      },
      deps: [PLANNED_INCOME_REPOSITORY, INCOME_REPOSITORY, GetCurrencyRateUseCase, TRANSACTION_MANAGER],
    },
  ];
}
